import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Navbar from "@/components/layout/Navbar";
import DashboardHeader from "@/components/layout/DashboardHeader";
import Footer from "@/components/layout/Footer";
import { BookOpen, ArrowRight, Search } from "lucide-react";
import { PinButton } from "@/components/PinContext";
import { useAuth } from "@/App";
import { toast } from "sonner";

const Blog = () => {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tags, setTags] = useState([]);
  const [selectedTag, setSelectedTag] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [notifyEmail, setNotifyEmail] = useState("");

  useEffect(() => {
    setLoading(true);
    const params = selectedTag ? `?tag=${selectedTag}` : "";
    axios.get(`${API}/blog${params}`)
      .then((res) => setPosts(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));

    axios.get(`${API}/blog/tags/all`)
      .then((res) => setTags(res.data))
      .catch(() => {});
  }, [selectedTag]);

  const filteredPosts = posts.filter((post) =>
    post.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    post.excerpt?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <div className="min-h-screen bg-white" data-testid="blog-page">
      <Navbar />
      <DashboardHeader />

      {/* Section heading — slim, contained */}
      <section className="pt-8 md:pt-10 pb-2">
        <div className="container-main">
          <p className="overline text-[#FF2D87] mb-2">Resources</p>
          <p className="text-sm md:text-base text-[#4A4A4A] max-w-2xl">
            Guides, tools, and creative insights — created by and for female and gender diverse creatives and professionals.
          </p>
        </div>
      </section>

      {/* Search + Tags — compact, on white */}
      <section className="py-5 border-b border-[#E5E5E5]">
        <div className="container-main">
          <div className="flex flex-col md:flex-row gap-4 justify-between items-start md:items-center">
            <div className="relative w-full md:w-auto md:min-w-[300px]">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#4A4A4A]" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search resources…"
                className="w-full pl-12 pr-4 py-2.5 border-2 border-[#0A0A0A] bg-white focus:outline-none focus:border-[#FF2D87] text-sm"
                data-testid="blog-search-input"
              />
            </div>

            <div className="flex flex-wrap gap-2" data-testid="blog-tags-filter">
              <button
                onClick={() => setSelectedTag("")}
                data-testid="blog-tag-all"
                className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest border-2 border-[#0A0A0A] rounded-full transition-colors ${!selectedTag ? "bg-[#0A0A0A] text-white" : "bg-white text-[#0A0A0A] hover:bg-[#FFC2E8]"}`}
              >
                All
              </button>
              {tags.map((tag) => (
                <button
                  key={tag}
                  onClick={() => setSelectedTag(tag)}
                  data-testid={`blog-tag-${tag}`}
                  className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest border-2 border-[#0A0A0A] rounded-full transition-colors ${selectedTag === tag ? "bg-[#0A0A0A] text-white" : "bg-white text-[#0A0A0A] hover:bg-[#FFC2E8]"}`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Posts */}
      <section className="py-10 md:py-14">
        <div className="container-main">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="card-brutal overflow-hidden bg-white">
                  <div className="skeleton aspect-[16/10]" />
                  <div className="p-6">
                    <div className="skeleton h-6 w-3/4 mb-2" />
                    <div className="skeleton h-4 w-full mb-1" />
                    <div className="skeleton h-4 w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center py-12">
              <div className="max-w-md mx-auto bg-white border-2 border-[#0A0A0A] p-8 shadow-[6px_6px_0px_0px_rgba(10,10,10,1)]">
                <div className="w-16 h-16 bg-[#FFC2E8] border-2 border-[#0A0A0A] flex items-center justify-center mx-auto mb-5">
                  <BookOpen className="w-8 h-8 text-[#FF2D87]" />
                </div>
                <h2 className="text-2xl font-display font-bold mb-3 text-[#0A0A0A]">Coming soon</h2>
                <p className="text-[#0A0A0A]/80 mb-6">
                  Guides, templates, and creative insights are on the way. Sign up to be the first to know when we publish.
                </p>
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  if (!notifyEmail) return;
                  try {
                    await axios.post(`${API}/newsletter/subscribe`, { email: notifyEmail });
                    toast.success("You'll be notified when we publish!");
                    setNotifyEmail("");
                  } catch {
                    toast.error("Something went wrong");
                  }
                }} className="flex flex-col sm:flex-row gap-3 max-w-sm mx-auto">
                  <input
                    type="email"
                    value={notifyEmail}
                    onChange={(e) => setNotifyEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="flex-1 px-4 py-3 border-2 border-[#0A0A0A] bg-white focus:outline-none focus:border-[#FF2D87]"
                    data-testid="blog-notify-email"
                  />
                  <button className="btn-primary text-xs py-3 px-6" data-testid="blog-notify-btn">
                    Notify Me
                  </button>
                </form>
              </div>
            </div>
          ) : (
            <>
              {/* Featured (first post) — magazine hero */}
              {(() => {
                const featured = filteredPosts[0];
                return (
                  <article className="relative grid grid-cols-1 lg:grid-cols-12 border-2 border-[#0A0A0A] overflow-hidden mb-12 shadow-[8px_8px_0_#FF2D87]" data-testid={`blog-featured-${featured.slug}`}>
                    <Link to={`/blog/${featured.slug}`} className="lg:col-span-7 relative bg-[#FFC2E8] min-h-[260px] lg:min-h-[420px] overflow-hidden">
                      {featured.image_url ? (
                        <img src={featured.image_url} alt={featured.title} className="absolute inset-0 w-full h-full object-cover" />
                      ) : (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <BookOpen className="w-32 h-32 text-[#FF2D87]/40" />
                        </div>
                      )}
                      <div className="absolute top-5 left-5">
                        <p className="text-[10px] font-bold uppercase tracking-[0.3em] bg-[#0A0A0A] text-[#FF2D87] px-3 py-1.5">★ Featured read</p>
                      </div>
                      {user && (
                        <div className="absolute top-5 right-5">
                          <PinButton targetType="post" targetId={featured.post_id} className="w-9 h-9 rounded-full" onLight />
                        </div>
                      )}
                    </Link>
                    <div className="lg:col-span-5 p-6 md:p-8 bg-white flex flex-col">
                      <div className="flex flex-wrap gap-2 mb-4">
                        {featured.tags?.slice(0, 3).map((tag) => (
                          <span key={tag} className="text-[10px] font-bold uppercase tracking-widest border border-[#0A0A0A] px-2 py-0.5">{tag}</span>
                        ))}
                      </div>
                      <h2 className="text-2xl md:text-3xl lg:text-4xl font-display font-bold leading-tight mb-3 text-[#0A0A0A]">{featured.title}</h2>
                      <p className="text-[#4A4A4A] mb-5 line-clamp-3">{featured.excerpt}</p>
                      <div className="mt-auto flex items-center justify-between">
                        <span className="text-xs font-mono text-[#4A4A4A]">
                          {!featured.hide_author && featured.author_name}
                          {!featured.hide_author && !featured.hide_date && " · "}
                          {!featured.hide_date && formatDate(featured.created_at)}
                        </span>
                        <Link to={`/blog/${featured.slug}`} className="btn-primary inline-flex items-center gap-2" data-testid={`read-featured-${featured.slug}`}>
                          Read story <ArrowRight className="w-4 h-4" />
                        </Link>
                      </div>
                    </div>
                  </article>
                );
              })()}

              {/* Rest of the journal — editorial grid */}
              {filteredPosts.length > 1 && (
                <>
                  <div className="flex items-baseline justify-between mb-5 border-b-2 border-[#0A0A0A] pb-2">
                    <h3 className="font-display font-bold text-xl text-[#0A0A0A]">More from the journal</h3>
                    <span className="font-mono text-xs text-[#4A4A4A]">{filteredPosts.length - 1} {filteredPosts.length - 1 === 1 ? "article" : "articles"}</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredPosts.slice(1).map((post) => (
                      <article
                        key={post.post_id}
                        className="group relative bg-white border-2 border-[#0A0A0A] overflow-hidden hover:-translate-y-1 hover:shadow-[6px_6px_0_#0A0A0A] transition-all flex flex-col"
                        data-testid={`blog-card-${post.slug}`}
                      >
                        {user && (
                          <div className="absolute top-3 right-3 z-10">
                            <PinButton targetType="post" targetId={post.post_id} className="w-8 h-8 rounded-full" onLight />
                          </div>
                        )}
                        <Link to={`/blog/${post.slug}`} className="block">
                          <div className="aspect-[16/10] overflow-hidden bg-[#FFC2E8]">
                            {post.image_url ? (
                              <img src={post.image_url} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <BookOpen className="w-12 h-12 text-[#FF2D87]/50" />
                              </div>
                            )}
                          </div>
                        </Link>
                        <div className="p-5 flex flex-col flex-1">
                          <div className="flex flex-wrap gap-1.5 mb-2">
                            {post.tags?.slice(0, 2).map((tag) => (
                              <span key={tag} className="text-[10px] font-bold uppercase tracking-widest border border-[#0A0A0A] px-2 py-0.5">{tag}</span>
                            ))}
                          </div>
                          <Link to={`/blog/${post.slug}`}>
                            <h3 className="text-lg md:text-xl font-display font-bold leading-snug mb-2 line-clamp-2 hover:text-[#FF2D87] transition-colors">{post.title}</h3>
                          </Link>
                          <p className="text-sm text-[#4A4A4A] line-clamp-2 mb-4">{post.excerpt}</p>
                          <div className="mt-auto flex items-center justify-between">
                            <span className="text-[10px] font-mono uppercase tracking-widest text-[#4A4A4A]">
                              {!post.hide_date && formatDate(post.created_at)}
                            </span>
                            <Link to={`/blog/${post.slug}`} className="text-xs font-bold uppercase tracking-widest text-[#0A0A0A] hover:text-[#FF2D87] inline-flex items-center gap-1">
                              Read <ArrowRight className="w-3.5 h-3.5" />
                            </Link>
                          </div>
                        </div>
                      </article>
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Blog;
